import { AxiosError } from "axios";
import { MapPin } from "lucide-react";

import { useCheckIn, useShiftEvents } from "@/features/shift-events/model/useShiftEvents";
import { CheckOutSummary } from "@/features/shift-events/ui/CheckOutSummary";
import { formatIsoDateTime12h } from "@/shared/lib/time";

interface Props {
  shiftId: string;
  shiftStatus: string;
  /** Full venue address shown above the Check-in button so the worker can
   * confirm where to be — and so geofence-distance failures are easier to
   * diagnose ("you're at X, the venue is at Y"). */
  venueAddress?: string;
}

// Test Diner (Ottawa) seeded coords — used by the dev-only "simulate at venue" button
// so the demo doesn't require DevTools Sensors fiddling.
const DEMO_VENUE_COORDS = { gps_lat: 45.4215, gps_lng: -75.6972 };
const IS_DEV = import.meta.env.DEV;

export function CheckInOutPanel({ shiftId, shiftStatus, venueAddress }: Props) {
  const { data: events } = useShiftEvents(shiftId);
  const checkIn = useCheckIn(shiftId);

  const hasCheckedIn = events?.some((e) => e.event_type === "CHECK_IN");
  const hasCheckedOut = events?.some((e) => e.event_type === "CHECK_OUT");

  const checkInEvent = events?.find((e) => e.event_type === "CHECK_IN");
  const checkOutEvent = events?.find((e) => e.event_type === "CHECK_OUT");

  const error = checkIn.error;
  const errMsg = error
    ? error instanceof AxiosError
      ? (error.response?.data?.detail ?? "Something went wrong")
      : error.message
    : null;

  if (shiftStatus === "COMPLETED") {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm">
        <div className="font-medium">Shift completed</div>
        <div className="mt-1 text-muted-foreground">
          Checked in: {checkInEvent ? formatIsoDateTime12h(checkInEvent.created_at) : "—"}
        </div>
        <div className="text-muted-foreground">
          Checked out: {checkOutEvent ? formatIsoDateTime12h(checkOutEvent.created_at) : "—"}
        </div>
      </div>
    );
  }

  if (shiftStatus !== "FILLED") {
    return null;
  }

  if (!hasCheckedIn) {
    return (
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <h3 className="font-semibold">Check-in</h3>
        {venueAddress && (
          <p className="flex items-start gap-1.5 text-sm text-copy-muted">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-copy-subtle" />
            <span className="break-words">{venueAddress}</span>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venueAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 text-primary hover:underline"
            >
              Directions
            </a>
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => checkIn.mutate(undefined)}
            disabled={checkIn.isPending}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {checkIn.isPending ? "Checking in..." : "Check in"}
          </button>
          {IS_DEV && (
            <button
              onClick={() => checkIn.mutate(DEMO_VENUE_COORDS)}
              disabled={checkIn.isPending}
              title="Dev only: sends Test Diner's Ottawa coords directly so the geofence passes without DevTools Sensors."
              className="rounded-md border border-dashed border-muted-foreground/50 px-3 py-2 text-xs text-muted-foreground hover:bg-surface-sunken disabled:opacity-50"
            >
              🎭 Simulate at venue
            </button>
          )}
        </div>
        {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
      </div>
    );
  }

  if (hasCheckedIn && !hasCheckedOut) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border bg-card p-4 text-sm">
          <p className="text-sm">Checked in at {formatIsoDateTime12h(checkInEvent!.created_at)}</p>
        </div>
        <CheckOutSummary
          shiftId={shiftId}
          devOverrideCoords={IS_DEV ? DEMO_VENUE_COORDS : undefined}
        />
      </div>
    );
  }

  return null;
}
